import { useState } from "react";
import { CommercialODApplicationDocsEnum, FileType } from "../../../../models";
import { useDispatch } from "react-redux";
import dispatch from "../../../../middleware";
import {
  getCommercialODDocument,
  uploadCommercilODApplicationDocument,
} from "../../../../actions/application.action";
import Modal from "../../../../components/modal";
import DocumentViewer from "../../../../components/document-viewer";
import {
  getDataUrlFromDocTypeAndBody,
  splitKeyWithSpace,
} from "../../../../utils";
import DocumentUpload from "../../../../components/document-upload";
import CommercialODApplicationDocumentList from "./commercialODApplicationDocumentLists";
import { CommercialODDoc } from "../../../../dto/commercial.dto";

interface CommercialODApplicationDocumentUploadProps {
  appId: string;
}

export default function CommercialODApplicationDocumentUpload({
  appId,
}: CommercialODApplicationDocumentUploadProps) {
  const storeDispatch = useDispatch();
  const [uploadModal, setUploadModal] = useState<{
    active: boolean;
    docName: string;
  }>({ active: false, docName: "" });
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
    active: boolean;
  }>({ title: "", message: "", active: false });
  const [isDocumentPreviewing, setIsDocumentPreviewing] = useState<{
    [docName: string]: boolean;
  }>({});
  const [documentViewer, setDocumentViewer] = useState<{
    active: boolean;
    docUrl?: string;
    docType?: FileType;
  }>({ active: false });

  const DOCUMENTS = [
    {
      name: CommercialODApplicationDocsEnum.ITR,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.BANK_STATEMENT,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.DEBTOR_DETAILS,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.FINANCIAL_STATEMENTS,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.GST_DOCUMENTS,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.INCORPORATION_CERTIFICATE,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.LOAN_DETAILS_WITH_REPAYMENT_SCHEDULES,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.OD_STATEMENT,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.PAYROLL_DETAILS,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.PROVISIONAL_OF_FINANCIALS,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
    {
      name: CommercialODApplicationDocsEnum.SIGNED_LENDER_AGREEMENT,
      previewDocument: handlePreviewDocument,
      uploadDocument: handleUploadDocument,
    },
  ];

  async function handlePreviewDocument(docName: string) {
    setIsDocumentPreviewing((prevState) => ({
      ...prevState,
      [docName]: true,
    }));
    try {
      const { type, body }: CommercialODDoc = await dispatch(
        storeDispatch,
        getCommercialODDocument(appId, docName)
      );

      // API returns the mime type as a string; FileType is a string enum of those mimes.
      const docType = type as FileType;
      setDocumentViewer({
        active: true,
        docUrl: getDataUrlFromDocTypeAndBody(type, body),
        docType,
      });
      // RBI AUDIT: Document must be previewed in-portal instead of downloaded
      // to the user's device (regulatory requirement). Original download logic
      // is commented out below (not removed) for audit trail.
      // downloadFileFromBase64({
      //   base64: body,
      //   fileName: docName,
      //   fileType: type,
      // });
    } catch (error) {
      setErrorModal({
        title: "Invoice List",
        message: error?.message ?? "Something Went Wrong!!",
        active: true,
      });
    } finally {
      setIsDocumentPreviewing((prevState) => ({
        ...prevState,
        [docName]: false,
      }));
    }
  }

  function handleUploadDocument(docName: string) {
    setUploadModal({ active: true, docName });
  }

  async function handleFileUpload(fileInB64: string, fileType: string) {
    const payload = {
      file: fileInB64,
      documentName: uploadModal.docName,
      contentType: fileType,
      password: "",
    };

    await dispatch(
      storeDispatch,
      uploadCommercilODApplicationDocument(appId, payload)
    );
  }

  return (
    <>
      <CommercialODApplicationDocumentList
        documents={DOCUMENTS}
        isDocPreviewing={isDocumentPreviewing}
      />
      <Modal
        open={uploadModal.active}
        title={`Upload ${splitKeyWithSpace(uploadModal.docName)}`}
        onClose={() =>
          setUploadModal((prevState) => ({ ...prevState, active: false }))
        }
        hasCancelButton={false}
        hasSubmitButton={false}
      >
        <DocumentUpload
          accepts={[FileType.APP_PDF, FileType.IMG_PNG]}
          onFileSubmit={handleFileUpload}
        />
      </Modal>
      <Modal
        open={errorModal.active}
        title={errorModal.title}
        onClose={() =>
          setErrorModal((prevError) => ({ ...prevError, active: false }))
        }
        hasCancelButton={false}
        hasSubmitButton={false}
      >
        {errorModal.message}
      </Modal>
      <DocumentViewer
        {...documentViewer}
        onClose={() => setDocumentViewer({ active: false })}
      />
    </>
  );
}
